// Translates between the fleet UI's 7-boolean "days available" shape and the resource_rule_mappings
// recurrence rows that drive availability (ENG-6065). scheduleResource() is the consumer, so these
// have to match its semantics exactly:
//   DAILY    -> every day
//   WEEKDAYS -> dow 1..5, regardless of rule_start's weekday
//   WEEKLY   -> only when day.dow === rule_start.getUTCDay()

// Index = getUTCDay(), so DAY_NAMES[3] === 'wednesday'.
export const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const WEEKDAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

export const EMPTY_DAYS = Object.freeze(Object.fromEntries(DAY_NAMES.map((day) => [day, false])));

// Live or yet to start: everything that still schedules a day from `asOf` on.
//
// rule_end is compared strictly, matching scheduleResource's overlaps(): a rule ending at a day's
// start does not cover that day.
export const notEnded = (rule, asOf) => !!rule && (!rule.rule_end || new Date(rule.rule_end) > asOf);

// UTC midnight, so getUTCDay() on the result is stable regardless of the viewer's timezone.
const utcMidnight = (date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

const DAY_MS = 86400000;

// Backwards by default, so rule_start stays <= the caller's floor and availability on past dates
// survives. Forwards when the floor is where a schedule starts rather than one it preserves — a
// backward-aligned start there schedules the weekdays of the week before it.
const alignToDow = (from, dow, forward) => {
    const base = utcMidnight(from);
    const shift = forward ? (dow - base.getUTCDay() + 7) % 7 : -((base.getUTCDay() - dow + 7) % 7);
    return new Date(base.getTime() + shift * DAY_MS);
};

// Collapsed to the fewest rows scheduleResource can consume. Callers backdate `floor` so existing
// routes on past dates keep resolving; `forward` starts each weekday on or after it instead.
export const rulesFromDays = (days = {}, floor = new Date(), { forward = false } = {}) => {
    const enabled = DAY_NAMES.filter((day) => !!days[day]);
    if (enabled.length === 0) return [];

    const start = utcMidnight(new Date(floor)).toISOString();

    if (enabled.length === 7) {
        return [{ rule_start: start, rule_type: 'DAILY' }];
    }

    const isWeekdaysOnly = WEEKDAY_NAMES.every((day) => !!days[day]) && !days.saturday && !days.sunday;
    if (isWeekdaysOnly) {
        return [{ rule_start: start, rule_type: 'WEEKDAYS' }];
    }

    // One WEEKLY rule per selected day, each starting on the weekday it represents.
    return enabled.map((day) => ({
        rule_start: alignToDow(new Date(floor), DAY_NAMES.indexOf(day), forward).toISOString(),
        rule_type: 'WEEKLY',
    }));
};

// Lets callers skip the rule rewrite when the modal was saved without changes.
export const sameDays = (a = {}, b = {}) => DAY_NAMES.every((day) => !!a[day] === !!b[day]);
